import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import LyricConfirm from './LyricConfirm';
import './AiApiModal.css';

/**
 * Owner/admin panel to manage a deployed app's "Ask Lyric" AI API access.
 *
 * Lets the app owner enable the AI API (mints a per-app key), view/copy/rotate
 * that key, disable it, and copy a ready-to-run usage snippet. The app then
 * POSTs to /api/ai-api/ask with the key to reach Lyric's shared Gemini engine
 * (the same one behind the in-app "Ask Lyric" popup). See server/routes/aiApi.js.
 *
 * Props:
 *   appName     registry key of the app
 *   displayName friendly name (for the header + snippet)
 *   apiUrl      API base URL
 *   authFetch   (url, opts) => fetch, injects the Okta bearer token
 *   onClose     close the modal
 */
const AiApiModal = ({ appName, displayName, apiUrl, authFetch, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null); // { aiEnabled, apiKey, aiConfigured, models, defaultModel, rateLimit }
  const [busy, setBusy] = useState(false);
  const [revealKey, setRevealKey] = useState(false);
  const [copied, setCopied] = useState('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [redeployNeeded, setRedeployNeeded] = useState(false);
  const [redeploying, setRedeploying] = useState(false);
  const [applied, setApplied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/ai`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, appName, authFetch]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape (matches the app-wide modal behavior).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirmRotate) onClose(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, confirmRotate]);

  const act = useCallback(async (path, label) => {
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/ai/${path}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await load();
      if (label === 'rotate-key' || label === 'enable') setRevealKey(true);
      // enable/rotate return redeployRequired when the app is live — its env is
      // only re-read on respawn, so surface the "Redeploy now" prompt.
      if (label === 'disable') { setRedeployNeeded(false); setApplied(false); }
      else { setRedeployNeeded(!!data.redeployRequired); setApplied(false); }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [apiUrl, appName, authFetch, load]);

  const applyRedeploy = useCallback(async () => {
    setRedeploying(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/ai/apply`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRedeployNeeded(false);
      setApplied(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeploying(false);
    }
  }, [apiUrl, appName, authFetch]);

  const copy = useCallback(async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard blocked — user can select manually */ }
  }, []);

  const key = status?.apiKey || '';
  const enabled = !!status?.aiEnabled;
  const maskedKey = key ? `${key.slice(0, 9)}${'•'.repeat(Math.max(0, key.length - 13))}${key.slice(-4)}` : '';
  const askUrl = `${apiUrl}/api/ai-api/ask`;

  // Always reference the injected env var in the snippet so "Copy" produces a
  // runnable command without embedding (or masking) the secret key.
  const curlSnippet = key
    ? `curl -X POST ${askUrl} \\
  -H "Authorization: Bearer $LYRIC_AI_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message": "Summarize this changelog in one sentence: ...",
    "model": "${status?.defaultModel || 'gemini-2.5-flash'}"
  }'`
    : '';

  return ReactDOM.createPortal(
    <div className="ai-api-overlay" onClick={(e) => { if (e.target === e.currentTarget && !confirmRotate) onClose(); }}>
      <div className="ai-api-card" role="dialog" aria-modal="true" aria-label="Ask Lyric API settings">
        <div className="ai-api-header">
          <span className="ai-api-brand" aria-hidden="true">🤖</span>
          <div>
            <h3>Ask Lyric API</h3>
            <div className="ai-api-sub">{displayName || appName}</div>
          </div>
          <button className="ai-api-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="ai-api-body">
          {loading ? (
            <div className="ai-api-muted">Loading…</div>
          ) : error ? (
            <div className="ai-api-error">{error}</div>
          ) : (
            <>
              {status && !status.aiConfigured && (
                <div className="ai-api-warn">
                  ⚠️ Gemini isn’t configured on the Lyric server yet — enabling here works, but asks will
                  fail with 503 until an admin sets <code>GEMINI_API_KEY</code>.
                </div>
              )}

              <div className="ai-api-row">
                <span className="ai-api-label">Status</span>
                <span className={`ai-api-badge ${enabled ? 'on' : 'off'}`}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              {enabled && key && (
                <div className="ai-api-keybox">
                  <span className="ai-api-label">API key</span>
                  <code className="ai-api-key">{revealKey ? key : maskedKey}</code>
                  <div className="ai-api-keybtns">
                    <button onClick={() => setRevealKey((v) => !v)}>{revealKey ? 'Hide' : 'Reveal'}</button>
                    <button onClick={() => copy(key, 'key')}>{copied === 'key' ? 'Copied!' : 'Copy'}</button>
                  </div>
                </div>
              )}

              {enabled && key && (
                <div className="ai-api-inject">
                  ✓ Lyric injects this key into your app automatically as the{' '}
                  <code>LYRIC_AI_API_KEY</code> environment variable — no secret to set by hand.
                  It takes effect on the app’s next (re)deploy.
                </div>
              )}

              {redeployNeeded && (
                <div className="ai-api-redeploy">
                  <div className="ai-api-redeploy-text">
                    Redeploy <b>{displayName || appName}</b> to apply the key to the running app.
                  </div>
                  <button className="ai-api-btn primary" disabled={redeploying} onClick={applyRedeploy}>
                    {redeploying ? 'Redeploying…' : 'Redeploy now'}
                  </button>
                </div>
              )}

              {applied && !redeployNeeded && (
                <div className="ai-api-inject">
                  🚀 Redeploy started — the key is injected on respawn. Give it a moment, then test an ask.
                </div>
              )}

              <div className="ai-api-meta">
                {status?.defaultModel && <span>Default model: <b>{status.defaultModel}</b></span>}
                {status?.rateLimit && (
                  <span>Limit: <b>{status.rateLimit.max}</b> / {Math.round((status.rateLimit.windowMs || 0) / 60000)} min</span>
                )}
              </div>

              {enabled && key && (
                <div className="ai-api-snippet">
                  <div className="ai-api-snippet-head">
                    <span className="ai-api-label">Usage</span>
                    <button onClick={() => copy(curlSnippet, 'curl')}>{copied === 'curl' ? 'Copied!' : 'Copy'}</button>
                  </div>
                  <pre>{curlSnippet}</pre>
                  <div className="ai-api-muted small">
                    POST to <code>/api/ai-api/ask</code> with a JSON body: <code>message</code> (required),
                    optional <code>context</code> and <code>model</code>. The response is{' '}
                    <code>{'{ ok, response, modelUsed, remaining }'}</code>. In code, read the key from{' '}
                    <code>process.env.LYRIC_AI_API_KEY</code> (or your language’s equivalent). Allowed
                    models: {(status?.models || []).join(', ') || '—'}.
                  </div>
                </div>
              )}

              <div className="ai-api-actions">
                {!enabled ? (
                  <button className="ai-api-btn primary" disabled={busy} onClick={() => act('enable', 'enable')}>
                    {busy ? 'Enabling…' : 'Enable Ask Lyric API'}
                  </button>
                ) : (
                  <>
                    <button className="ai-api-btn" disabled={busy} onClick={() => setConfirmRotate(true)}>
                      Rotate key
                    </button>
                    <button className="ai-api-btn danger" disabled={busy} onClick={() => act('disable', 'disable')}>
                      {busy ? 'Disabling…' : 'Disable'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <LyricConfirm
        isOpen={confirmRotate}
        tone="danger"
        title="Rotate API key?"
        message="The current key stops working immediately. Any app or script using it must be updated with the new key."
        confirmLabel="Rotate key"
        onConfirm={() => { setConfirmRotate(false); act('rotate-key', 'rotate-key'); }}
        onCancel={() => setConfirmRotate(false)}
      />
    </div>,
    document.body,
  );
};

export default AiApiModal;
